import { normalizeFinding } from './severity';
import { assetFindingLink } from './assetFindingLinks';
import { persistedFindingAssetIds } from './findingOwnership';

export const ASSET_FINDINGS_PAGE_SIZE = 100;
export const LEGACY_HISTORY_WARNING = 'Compatibility mode: the deployed API cannot verify complete asset ownership or history. Only supported links are shown; totals remain unverified.';

const keyOf = value => typeof value === 'string' ? value.trim().toLowerCase() : '';
const rowId = row => row?.findingId ?? row?.persistedFindingId ?? row?.id;
const incomplete = () => new Error('The asset findings response was incomplete.');
const changed = () => new Error('Asset findings changed while paging. Refresh to load a consistent list.');

function normalizeRows(rows, clientId) {
  if (!Array.isArray(rows)) throw incomplete();
  const ids = rows.map(row => keyOf(rowId(row)));
  if (ids.some(id => !id) || new Set(ids).size !== ids.length ||
      rows.some(row => row.clientId != null && keyOf(row.clientId) !== keyOf(clientId))) throw incomplete();
  const normalized = rows.map(normalizeFinding);
  if (normalized.some((row, index) => !row || keyOf(row.id) !== ids[index])) throw incomplete();
  return normalized;
}

function readPage(result, page, clientId) {
  if (!Array.isArray(result?.findings) || !Number.isSafeInteger(result.total) || result.total < 0 ||
      result.page !== page || result.pageSize !== ASSET_FINDINGS_PAGE_SIZE ||
      result.findings.length > ASSET_FINDINGS_PAGE_SIZE) throw incomplete();
  const rows = normalizeRows(result.findings, clientId);
  const checked = (page - 1) * ASSET_FINDINGS_PAGE_SIZE + rows.length;
  if (checked > result.total || (checked < result.total && rows.length < ASSET_FINDINGS_PAGE_SIZE)) throw incomplete();
  return rows;
}

// Per-generation, per-tenant session; never caches API capabilities across assets/users.
// The old asset detail query is TOP (100), and its serializers omit persisted owners.
// Therefore even exhausting tenant pages cannot certify complete legacy asset history.
export function createAssetFindingsLoader(api, clientId, assetId, isCurrent = () => true, onProgress = () => {}) {
  const root = '/clients/' + encodeURIComponent(clientId);
  const assetPath = root + '/assets/' + encodeURIComponent(assetId);
  let mode = 'asset';
  let lastPage = 0;
  let sourceTotal = null;
  let checked = 0;
  let seen = new Set();
  let findings = [];
  let legacyAsset = null;
  let linked = new Map();

  const snapshot = () => ({
    mode, findings, links: [...linked.values()], page: lastPage,
    total: mode === 'asset' ? sourceTotal : null,
    sourceTotal, checked, loading: false, error: null,
    hasMore: sourceTotal === null ? mode === 'legacy' : checked < sourceTotal,
    complete: mode === 'asset' && sourceTotal !== null && checked === sourceTotal,
    coverageWarning: mode === 'legacy' ? LEGACY_HISTORY_WARNING : null,
  });

  const checkCurrent = () => {
    if (!isCurrent()) {
      const error = new Error('Asset findings request superseded.');
      error.name = 'AbortError';
      throw error;
    }
  };
  const get = async path => {
    checkCurrent();
    const result = await api.get(path);
    checkCurrent();
    return result;
  };

  return {
    async load(page) {
      checkCurrent();
      if (page !== lastPage + 1) throw changed();
      let result;
      if (mode === 'asset') {
        try {
          result = await get(assetPath + '/findings?page=' + page + '&pageSize=' + ASSET_FINDINGS_PAGE_SIZE);
        } catch (error) {
          checkCurrent();
          // Only the initial missing/unsupported route permits a compatibility probe.
          // Auth, network, throttling, malformed payloads, and later-page errors fail closed.
          if (page !== 1 || ![404, 405].includes(error?.status)) throw error;
          const asset = await get(assetPath);
          if (!asset || keyOf(asset.assetId) !== keyOf(assetId) ||
              keyOf(asset.clientId) !== keyOf(clientId)) throw incomplete();
          const summaries = normalizeRows(asset.findings, clientId);
          legacyAsset = asset;
          linked = new Map(summaries.map(row => assetFindingLink(asset, row)).filter(Boolean)
            .map(link => [keyOf(link.finding.id), link]));
          mode = 'legacy';
          findings = [...linked.values()].map(link => link.finding);
          // Scoped summaries are independently validated; tenant enrichment may stall or fail.
          onProgress({ ...snapshot(), loading: true });
        }
      }
      if (mode === 'legacy') {
        result = await get(root + '/findings?page=' + page + '&pageSize=' + ASSET_FINDINGS_PAGE_SIZE + '&includeSuppressed=true&activeOnly=false');
      }
      const rows = readPage(result, page, clientId);
      if (sourceTotal !== null && sourceTotal !== result.total) throw changed();
      if (rows.some(row => seen.has(keyOf(row.id)))) throw changed();

      const nextChecked = checked + rows.length;
      if (nextChecked > result.total) throw changed();
      // Commit the session only after the entire page passes validation.
      if (mode === 'legacy') {
        const nextLinked = new Map(linked);
        for (const row of rows) {
          const link = assetFindingLink(legacyAsset, row);
          if (link) {
            const summaryOwners = persistedFindingAssetIds(linked.get(keyOf(row.id))?.finding);
            // Preserve actual summary-array provenance when the older list serializer omits owners.
            // Explicit empty/nonmatching arrays on the full row are never overridden.
            const hydrated = persistedFindingAssetIds(row) === null && summaryOwners !== null
              ? { ...row, affectedAssetIds: summaryOwners, affectedAssetIdsProvenance: 'persisted-summary' }
              : row;
            nextLinked.set(keyOf(row.id), { ...link, finding: hydrated });
          }
          else nextLinked.delete(keyOf(row.id)); // Explicit non-owner beats an earlier summary.
        }
        linked = nextLinked;
        findings = [...linked.values()].map(link => link.finding);
      } else {
        findings = [...findings, ...rows];
      }
      seen = new Set([...seen, ...rows.map(row => keyOf(row.id))]);
      sourceTotal = result.total;
      checked = nextChecked;
      lastPage = page;
      return snapshot();
    },
  };
}
